import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Collapse from "@mui/material/Collapse";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Link from "@mui/material/Link";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Sparkles } from "lucide-mui";
import { type FormEvent, type ReactNode, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { api, type AgentRunPlan, type ProspectList } from "../../api/client.ts";
import { keys } from "../../api/queryKeys.ts";
import { useOrganization } from "../../contexts/OrganizationContext.tsx";
import { useTenant } from "../../contexts/TenantContext.tsx";
import { KIND_META, isActionKind } from "../../pages/workflows/editor/types.ts";
import { formatDate, formatNumber } from "../../utils/format.ts";
import ScopeToggle from "../filters/ScopeToggle.tsx";
import TenantProfileFields, {
  profilePayload,
  profileValues,
  type TenantProfileValues,
} from "../tenant-onboarding/TenantProfileFields.tsx";
import AdvancedModelFold from "./AdvancedModelFold.tsx";
import NewProspectsOnlyField from "./NewProspectsOnlyField.tsx";

type CollectionMode = "new" | "existing";

const sameProfile = (a: TenantProfileValues, b: TenantProfileValues) =>
  a.offering_text.trim() === b.offering_text.trim() &&
  a.icp_text.trim() === b.icp_text.trim() &&
  a.buyer_persona_text.trim() === b.buyer_persona_text.trim();

/** Collections an agent may deliver into: live, and not filled by a signal workflow. */
export function deliverableCollections(lists: ProspectList[]): ProspectList[] {
  return lists.filter(
    (l) => !l.archived && l.collection_type !== "signal" && l.origin?.kind !== "workflow",
  );
}

function Section({ label, action, children }: { label: string; action?: ReactNode; children: ReactNode }) {
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, minHeight: 24 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        {action}
      </Box>
      {children}
    </Box>
  );
}

/** What runs, as quiet intro text under the title: the tenant's order workflow (its manual run), or the built-in agent alone. */
function WhatRuns({ plan, tenantName }: { plan: AgentRunPlan; tenantName: string }) {
  const { workflow, agent } = plan;
  const steps = (workflow?.step_types ?? []).map((t) => (isActionKind(t) ? KIND_META[t].label : t));
  const notes = [
    steps.length > 0 && `Steps: ${steps.join(" → ")}`,
    workflow?.requires_approval && "Running it here approves its agent step",
  ].filter(Boolean);
  return (
    <Box sx={{ "& strong": { color: "text.primary", fontWeight: 600 } }}>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        Places an order for <strong>{tenantName}</strong> and runs{" "}
        <strong>{workflow?.name ?? agent?.name ?? "the prospecting agent"}</strong> on it now
        {workflow ? ", once." : "."}
      </Typography>
      {notes.length > 0 && (
        <Typography variant="caption" component="div" sx={{ color: "text.secondary", mt: 0.5 }}>
          {notes.join(" · ")}
        </Typography>
      )}
    </Box>
  );
}

/** One line of the brief, collapsed. */
function BriefLine({ label, text }: { label: string; text: string }) {
  return (
    <Box sx={{ display: "flex", gap: 1, minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: "text.secondary", width: 92, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="caption" noWrap sx={{ minWidth: 0, color: text ? "text.primary" : "text.disabled" }}>
        {text || "Not set"}
      </Typography>
    </Box>
  );
}

interface RunAgentDialogProps {
  onClose: () => void;
}

/**
 * "Run agent": place a prospect order for the selected tenant and run an agent
 * on it now, with the model picked. The manual counterpart of an order coming
 * in: the tenant's order workflow fires once (that is the run), or the
 * built-in prospecting agent runs alone when no workflow has an agent. The
 * agent delivers into a new collection unless an existing one is picked.
 * Mounted only while open.
 */
export default function RunAgentDialog({ onClose }: RunAgentDialogProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { selectedOrgId } = useOrganization();
  const { selectedTenantId, selectedTenant } = useTenant();
  const tenantName = selectedTenant?.name ?? "this tenant";

  const planQuery = useQuery({
    queryKey: keys.prospectOrder.agentRunPlan(selectedOrgId ?? "", selectedTenantId ?? ""),
    queryFn: () => api.getAgentRunPlan(selectedOrgId!, selectedTenantId!),
    enabled: !!selectedOrgId && !!selectedTenantId,
  });
  const tenantQuery = useQuery({
    queryKey: keys.tenant.detail(selectedTenantId ?? ""),
    queryFn: () => api.getTenant(selectedTenantId!),
    enabled: !!selectedTenantId,
  });
  const listsQuery = useQuery({
    queryKey: keys.prospectList.list(selectedTenantId ?? ""),
    queryFn: () => api.getProspectLists(selectedTenantId!),
    enabled: !!selectedTenantId,
  });
  const plan = planQuery.data;
  const collections = useMemo(() => deliverableCollections(listsQuery.data ?? []), [listsQuery.data]);

  const [title, setTitle] = useState(() => `${tenantName} prospects · ${formatDate(new Date())}`);
  const [requestedCount, setRequestedCount] = useState("");
  const [newOnly, setNewOnly] = useState(false);
  const profileDefault = useMemo(() => profileValues(tenantQuery.data ?? selectedTenant), [tenantQuery.data, selectedTenant]);
  const [profile, setProfile] = useState<TenantProfileValues | null>(null);
  const [editingBrief, setEditingBrief] = useState(false);
  const [model, setModel] = useState("");
  const [mode, setMode] = useState<CollectionMode>("new");
  const [collectionName, setCollectionName] = useState("");
  const [existing, setExisting] = useState<ProspectList | null>(null);

  const brief = profile ?? profileDefault;
  const briefEdited = profile !== null && !sameProfile(profile, profileDefault);
  const profileEmpty = !profileDefault.offering_text && !profileDefault.icp_text && !profileDefault.buyer_persona_text;
  const showBriefFields = editingBrief || (tenantQuery.isSuccess && profileEmpty);

  const mutation = useMutation({
    mutationFn: () => {
      const count = Number(requestedCount.trim());
      return api.runAgentOnNewOrder(selectedOrgId!, {
        tenant_id: selectedTenantId!,
        title: title.trim(),
        // Unedited = blank: the brief then reads the tenant's profile as it is.
        ...(briefEdited ? profilePayload(brief) : {}),
        requested_count: requestedCount.trim() && Number.isFinite(count) ? count : null,
        new_prospects_only: newOnly,
        // Blank = the default (the agent's own model, else the newest Sonnet);
        // a pick applies to every agent on the order.
        model: model || null,
        collection_id: mode === "existing" ? existing?.id ?? null : null,
        collection_name: mode === "new" ? collectionName.trim() || null : null,
      });
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: keys.prospectOrder.all });
      queryClient.invalidateQueries({ queryKey: keys.prospectList.list(selectedTenantId!) });
      toast.success(
        result.needs_approval
          ? "Order placed; its agent step is waiting for approval"
          : result.agent_run_id
            ? "Agent started"
            : "Order placed",
      );
      onClose();
      navigate(`/organization/orders/${result.order.id}`);
    },
  });

  const blocker = !selectedTenantId
    ? "Select a tenant in the sidebar first."
    : !selectedOrgId
      ? "Select an organization first."
      : plan && !plan.owned
        ? `Only ${plan.owner_name ?? "the organization that owns this tenant"} can run agents for ${tenantName}; the runs bill its Claude plan.`
        : plan && !plan.credential_ok
          ? "no-token"
          : plan && !plan.agent
            ? "The prospecting agent is disabled."
            : null;
  const canSubmit =
    !!plan &&
    !blocker &&
    title.trim().length > 0 &&
    (mode === "new" || !!existing) &&
    !mutation.isPending;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (canSubmit) mutation.mutate();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { component: "form", onSubmit: submit } as object }}
    >
      <DialogTitle>Run agent</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          {planQuery.isLoading || !plan ? (
            <Box>
              <Skeleton variant="text" width="85%" />
              <Skeleton variant="text" width="45%" sx={{ fontSize: "0.75rem" }} />
            </Box>
          ) : (
            <WhatRuns plan={plan} tenantName={tenantName} />
          )}

          {blocker === "no-token" ? (
            <Alert severity="warning">
              Your organization has no Claude session token, so agents can't run.{" "}
              <Link component={RouterLink} to="/organization/integrations" onClick={onClose}>
                Set one under Integrations
              </Link>
            </Alert>
          ) : (
            blocker && <Alert severity="warning">{blocker}</Alert>
          )}

          <Section label="Order">
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                fullWidth
                size="small"
                autoFocus
              />
              <TextField
                label="How many companies"
                type="number"
                value={requestedCount}
                onChange={(e) => setRequestedCount(e.target.value)}
                size="small"
                placeholder="Agent decides"
                sx={{ width: { sm: 200 }, flexShrink: 0 }}
                slotProps={{ htmlInput: { min: 1 }, inputLabel: { shrink: true } }}
              />
            </Stack>
            <Box sx={{ mt: 0.5 }}>
              <NewProspectsOnlyField
                checked={newOnly}
                onChange={setNewOnly}
                tenantName={tenantName}
                disabled={mutation.isPending}
              />
            </Box>
          </Section>

          <Section
            label="Brief"
            action={
              briefEdited && (
                <Link component="button" type="button" variant="caption" onClick={() => setProfile(null)}>
                  Reset to profile
                </Link>
              )
            }
          >
            <Collapse in={!showBriefFields} unmountOnExit>
              <Card sx={{ "&:hover .brief-edit, &:focus-within .brief-edit": { color: "primary.main" } }}>
                <CardActionArea
                  onClick={() => setEditingBrief(true)}
                  disabled={tenantQuery.isLoading || mutation.isPending}
                  aria-label="Edit the brief for this run"
                  sx={{ p: 1.5, display: "flex", alignItems: "flex-start", gap: 1.5 }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {tenantQuery.isLoading ? (
                      <Skeleton height={60} />
                    ) : (
                      <Stack spacing={0.5}>
                        <BriefLine label="Sells" text={brief.offering_text} />
                        <BriefLine label="Ideal customer" text={brief.icp_text} />
                        <BriefLine label="Buyer persona" text={brief.buyer_persona_text} />
                      </Stack>
                    )}
                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
                      From {tenantName}'s profile
                    </Typography>
                  </Box>
                  <Pencil
                    className="brief-edit"
                    sx={{
                      fontSize: 16,
                      color: "text.secondary",
                      flexShrink: 0,
                      transition: (t) => t.transitions.create("color"),
                    }}
                  />
                </CardActionArea>
              </Card>
            </Collapse>
            <Collapse in={showBriefFields} unmountOnExit>
              <Box sx={{ pt: 1 }}>
                {profileEmpty && !briefEdited && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {tenantName} has no profile yet. Say what it sells and who buys it, or the agent
                    has little to work from.
                  </Alert>
                )}
                <TenantProfileFields values={brief} onChange={setProfile} minRows={2} />
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
                  {briefEdited
                    ? "For this run only; the tenant's profile is unchanged."
                    : profileEmpty
                      ? "For this run only. Fill in the tenant's profile under Settings to reuse it."
                      : "A blank field uses the tenant's profile."}
                </Typography>
              </Box>
            </Collapse>
          </Section>

          <Section
            label="Deliver to"
            action={
              <ScopeToggle
                aria-label="Collection"
                value={mode}
                onChange={setMode}
                options={[
                  { value: "new", label: "New collection" },
                  { value: "existing", label: "Existing" },
                ]}
              />
            }
          >
            {mode === "new" ? (
              <TextField
                label="Collection name"
                value={collectionName}
                onChange={(e) => setCollectionName(e.target.value)}
                placeholder={title.trim() || "Same as the title"}
                size="small"
                fullWidth
                helperText="A new collection for what this run finds"
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 240 } }}
              />
            ) : (
              <Autocomplete
                size="small"
                options={collections}
                value={existing}
                onChange={(_, next) => setExisting(next)}
                loading={listsQuery.isLoading}
                getOptionLabel={(o) => o.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                noOptionsText="No collections you can add to"
                renderOption={({ key, ...props }, o) => (
                  <li key={key} {...props}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", gap: 1 }}>
                      <Typography variant="body2" noWrap>
                        {o.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary", flexShrink: 0 }}>
                        {formatNumber(o.prospect_count)} prospects
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Collection"
                    helperText="Collections a signal workflow fills aren't listed"
                  />
                )}
              />
            )}
          </Section>

          {plan && (
            <AdvancedModelFold
              catalog={plan.models}
              value={model}
              onChange={setModel}
              inherited={plan.agent?.model}
              defaultLabel={plan.agent?.model ? "Agent's model" : "Default"}
              disabled={mutation.isPending}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          type="submit"
          variant="contained"
          startIcon={<Sparkles />}
          disabled={!canSubmit}
          loading={mutation.isPending}
        >
          Run agent
        </Button>
      </DialogActions>
    </Dialog>
  );
}
